<script setup lang="ts">
import GridLayout from '@/components/Layouts/GridLayout.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import { useGridSize } from '@/hooks/useGridSize'
import type { DropZoneData } from '@/types/components/DropZoneData'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import type {
    SocketCheckpointDetectionModel,
    SocketDetectionModel
} from '@/types/socket/SocketDetectionModel'
import { type Position } from '@vueuse/core'
import { computed, ref } from 'vue'
import SocketDetectionsWrapper from '../../../../socket/SocketDetectionsWrapper.vue'
import CheckpointCard from './components/CheckpointCard.vue'

type Props = {
    currentTabIndex: number
    data: CheckpointModel[]
    detections?: SocketCheckpointDetectionModel[]
    siteId: number
    partnerConfig?: PartnerModel
    editPermission: PermissionsEnums
    deletePermission: PermissionsEnums
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'goTo', value?: CheckpointModel): void
    (e: 'delete', checkpoint: CheckpointModel): void
}
defineEmits<Emits>()

const size = useGridSize()

const dropZone = ref<DropZoneData>({
    poiId: undefined,
    device: undefined,
    position: undefined
})
const dragStart = (device?: AssociatedDeviceModel, poiId?: number) => {
    dropZone.value.device = device
    dropZone.value.poiId = poiId
}

const dragEnd = (position?: Position) => (dropZone.value.position = position)

const handleClose = () => (dropZone.value.device = undefined)

const checkpointIds = computed(
    () => !!props.data.length && props.data?.map(item => item.id ?? item.id)
)

const deviceDetections = ref<SocketCheckpointDetectionModel[]>([])
const handleDetectionsUpdate = (value: SocketDetectionModel) =>
    (deviceDetections.value = value.checkpoint_detections)
</script>

<template>
    <SocketDetectionsWrapper
        v-if="checkpointIds"
        :poi_ids="checkpointIds"
        @update="handleDetectionsUpdate" />
    <StackLayout
        ref="stackRef"
        v-if="data?.length"
        direction="column"
        :gap="8"
        isResponsiveSmallScreen
        class="full-width">
        <GridLayout
            :gap="8"
            :maxItemPerRow="size"
            class="relative flex-wrap checkpoint-carousel"
            isResponsiveSmallScreen>
            <CheckpointCard
                v-for="(checkpoint, key) in data"
                :key="key"
                :data="checkpoint"
                :detections="
                    detections?.find(item => item.poi_id === checkpoint.id)?.detections ?? []
                "
                :editPermission="editPermission"
                :deletePermission="deletePermission"
                :dropZoneData="dropZone"
                @dragStart="dragStart"
                @dragEnd="dragEnd"
                @delete="$emit('delete', $event)"
                @click="$emit('goTo', $event)"
                @close="handleClose"
                @refresh="$emit('refresh')" />
        </GridLayout>
    </StackLayout>
    <StackLayout v-else class="align-self-center no-record">
        {{ $t('common.noRecord') }}
    </StackLayout>
</template>

<style scoped lang="scss">
.checkpoint-carousel {
    padding-bottom: 8px;

    & :deep(.checkpoint-dropdown) {
        cursor: default;
        min-width: 25%;

        & .card-content {
            pointer-events: none;
        }
        & .dropzone-card {
            & .card-footer {
                pointer-events: none;
            }
        }
    }
}
</style>
